// @ts-strict-ignore
import React, { useEffect, useMemo } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import {
  Box,
  Button,
  Callout,
  Combobox,
  Input,
  RowsSkeletonLoader,
  toast,
} from '@node-space/ui-components'
import { logSentryErrorAndGetEventId } from '@node-space/utils'
import { RequestError } from 'components/Errors/RequestError'
import { useAddCustomerWalletRequest } from 'hooks/mutations/useAddCustomerWalletMutation'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { currentCustomerAtom, customerModelAtom } from 'pages/Customers/atoms/customersAtoms'
import { CustomerModels } from 'pages/Customers/helpers/types'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { BaseErrorResponse } from 'types/beneficiaries'
import { AddCustomerWalletRequest } from 'types/customers'
import { Currencies } from 'types/types'
import { PayoutInstructionTypes } from 'types/unifiedPayouts/unifiedPayouts'
import { addWalletSchema, AddWalletSchemaProps } from '../../Wallets/schemas/addWalletSchema'
import { useFormOptions } from '../hooks/useFormOptions'
import { NoWalletProfilesCallout } from './CustomerWallet/NoWalletProfilesCallout'

interface AddWalletProps {
  handleClose: () => void
}

export const AddCustomerWallet = ({ handleClose }: AddWalletProps) => {
  const { t } = useTranslation()
  const { enableCustomerCryptoWalletCreation } = useFeatureFlags()

  const queryClient = useQueryClient()

  const customerModel = useAtomValue(customerModelAtom)
  const currentCustomer = useAtomValue(currentCustomerAtom)

  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()

  const isEmbeddedOrRelianceCustomerModel =
    customerModel === CustomerModels.EMBEDDED || customerModel === CustomerModels.RELIANCE

  const formSchema = addWalletSchema(t)
  const { control, formState, register, setValue, trigger, clearErrors } =
    useForm<AddWalletSchemaProps>({
      resolver: yupResolver(formSchema),
    })

  const [currency, walletName, walletProfileReference] = useWatch({
    control,
    name: ['currency', 'walletName', 'walletProfileReference'],
    defaultValue: {
      currency: '',
      walletName: '',
      walletProfileReference: '',
    },
  })

  const {
    walletProfileOptions,
    isFetchingWalletProfiles,
    walletProfilesError,
    profileMappings,
    availableCurrencies,
  } = useFormOptions(currency, isEmbeddedOrRelianceCustomerModel)

  useEffect(() => {
    isEmbeddedOrRelianceCustomerModel && setValue('currency', availableCurrencies?.[0]?.value)
  }, [isEmbeddedOrRelianceCustomerModel])

  useEffect(() => {
    if (currency && walletProfileOptions?.length === 1) {
      setValue('walletProfileReference', walletProfileOptions?.[0]?.value)
    }
  }, [currency, walletProfileOptions])

  useEffect(() => {
    if (walletProfilesError) {
      const error = walletProfilesError as BaseErrorResponse
      const sentryEventId = logSentryErrorAndGetEventId(
        `Failed API request to /ledger/v1/wallets/profiles with status ${error?.status} - useGetCustomersWalletProfileQuery`,
        error
      )
      setRequestError({
        errorCode: error.status,
        show: true,
        sentryTraceId: sentryEventId,
      })
    }
  }, [walletProfilesError])

  const { mutate: addCustomerWallet, isPending: isAddCustomerWalletLoading } =
    useAddCustomerWalletRequest()

  const handleAddWallet = async () => {
    clearErrors()
    resetRequestError()
    const isFormValid = await trigger(['currency', 'walletName'])

    if (isFormValid) {
      const postRequest: AddCustomerWalletRequest = {
        currencyCode: currency as Currencies,
        name: walletName,
        customerReference: currentCustomer?.reference,
        instruction: {
          walletProfile: walletProfileReference,
          type: isCryptoCurrency ? PayoutInstructionTypes?.CRYPTO : PayoutInstructionTypes?.FIAT,
          ...(!isCryptoCurrency && { virtualAccountRequired: true }),
        },
      }

      addCustomerWallet(postRequest, {
        onSuccess: async () => {
          handleClose()
          toast({
            header: t('customers.walletCreationMessage'),
            variant: 'success',
          })
          queryClient.invalidateQueries({ queryKey: [reactQueryName.CUSTOMER_WALLETS] })
        },
        onError: (error: BaseErrorResponse, data: AddCustomerWalletRequest) => {
          const sentryEventId = logSentryErrorAndGetEventId(
            `Failed API request to /ledger/v1/wallets with status ${error?.status} - useAddCustomerWalletRequest`,
            error,
            {
              data: typeof data === 'string' ? { message: data } : data,
            }
          )

          setRequestError({
            errorCode: error.status,
            show: true,
            sentryTraceId: sentryEventId,
          })
        },
      })
    }
  }

  const isAddingCustomerWalletEnabled = !!currency && !!walletName && !!walletProfileReference

  const handleCurrencyChange = (value: string) => {
    setValue('currency', value)

    if (value !== currency) {
      setValue('walletProfileReference', '')
    }

    if (enableCustomerCryptoWalletCreation && isCryptoCurrency) {
      setValue('walletProfileReference', walletProfileOptions?.[0]?.value)
    }
  }

  const isCryptoCurrency = useMemo(() => {
    if (!profileMappings || !currency) return false

    return profileMappings?.[currency]?.isCryptoProfile
  }, [currency, enableCustomerCryptoWalletCreation, profileMappings])

  if (isFetchingWalletProfiles) {
    return (
      <Box className="gap-0" flex flexCol width="full">
        <RowsSkeletonLoader rows={4} />
      </Box>
    )
  }

  return (
    <Box paddingX={24} paddingT={24} className="w-full space-y-6 text-gray-100">
      {requestError?.show && (
        <RequestError
          errorCode={requestError?.errorCode}
          sentryTraceId={requestError?.sentryTraceId}
        />
      )}

      {availableCurrencies?.length ? (
        <>
          <Box flexCol flexSize="fill" gapY={20}>
            <Combobox
              id="currency"
              {...register('currency')}
              label={t('customers.selectCurrency')}
              searchPlaceholder={t('searchOrSelectACurrency')}
              items={availableCurrencies}
              errorText={formState?.errors?.currency?.message}
              defaultValue={currency}
              onChange={handleCurrencyChange}
              filter={isEmbeddedOrRelianceCustomerModel && enableCustomerCryptoWalletCreation}
              loadingInline={isFetchingWalletProfiles}
            />

            {!!currency && (
              <>
                {!isCryptoCurrency && (
                  <Combobox
                    key={currency}
                    id="walletProfileReference"
                    {...register('walletProfileReference')}
                    label={t('customers.selectPaymenMethodsToSupport')}
                    searchPlaceholder={t('select')}
                    items={walletProfileOptions}
                    defaultValue={
                      currency && walletProfileOptions?.length === 1
                        ? walletProfileOptions?.[0]?.value
                        : walletProfileReference
                    }
                    onChange={value => setValue('walletProfileReference', value)}
                    disabled={!currency || (!!currency && walletProfileOptions?.length === 1)}
                    loadingInline={isFetchingWalletProfiles}
                    filter={false}
                  />
                )}

                <Input
                  {...register('walletName')}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setValue('walletName', e?.target?.value)
                  }
                  label={t('customers.walletName')}
                  placeholder={t('enterName')}
                  inputMode="text"
                  name="walletName"
                  testId="wallet-name"
                  errorText={formState?.errors?.walletName?.message}
                  error={!!formState?.errors?.walletName}
                  value={walletName}
                  disabled={!currency}
                />

                {!isCryptoCurrency && currency && (
                  <Callout variant="info">{t('customers.walletCreationFormMessage')}</Callout>
                )}
              </>
            )}
          </Box>

          <Box className="flex justify-between">
            <Button variant="secondary" onClick={handleClose}>
              {t('cancel')}
            </Button>
            <Button
              loading={isAddCustomerWalletLoading}
              disabled={!isAddingCustomerWalletEnabled}
              onClick={() => handleAddWallet()}
            >
              {t('continue')}
            </Button>
          </Box>
        </>
      ) : (
        <NoWalletProfilesCallout />
      )}
    </Box>
  )
}
